import { SetStateAction, useCallback, useEffect, useState } from 'react';
import {
  ApiResponse_string,
  AuthDTO,
  LoginUserDTO,
  TokenExchangeDTO,
} from '../services/apiClient';
import { useAuthApi } from './useAuthApi';
import { getPermissions, getToken, setTokens } from '../utils/tokenUtils';
import { getFirstAvailableRoute } from '../utils/routePermissions';
import { useNavigate } from 'react-router-dom';

interface UseLoginProps {
  setTempAccessToken?: (token: string) => void;
  setShowTwoFactorInput?: (show: boolean) => void;
  redirectPath?: string;
}

export const useLogin = ({
  setTempAccessToken,
  setShowTwoFactorInput,
  redirectPath,
}: UseLoginProps = {}) => {
  const { generateToken } = useAuthApi();
  const navigate = useNavigate();
  const [loading, setLoading] = useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const { generateTokenTwoFactor, googleTokenExchange } = useAuthApi();

  // Memoize the navigation function to prevent recreation on every render
  const navigateToLandingPage = useCallback(() => {
    if (redirectPath) {
      navigate(redirectPath);
      return;
    }
    const userPermissions = getPermissions() || [];
    const landingPage = getFirstAvailableRoute(userPermissions);
    navigate(landingPage);
  }, [navigate, redirectPath]);

  const handleLogin = async (email: string, password: string) => {
    const loginData: LoginUserDTO = {
      emailOrPhone: email || '',
      password: password || '',
    };

    try {
      setLoading(true);
      const response = await generateToken(loginData);
      setLoading(false);
      if (response) {
        handleSignInResponse(
          response,
          'Invalid credentials. Please try again.',
        );
      }
    } catch (error: any) {
      setLoading(false);
      if (error.message) {
        setErrorMessage(error.message);
      } else {
        setErrorMessage(error);
      }
    }
  };

  const handleTwoFactorVerification = async (email: string, tempAccessToken: string | null, twoFactorCode: string): Promise<void> => {

    try {
      if (!tempAccessToken) {
        setErrorMessage('Session expired. Please log in again.');
        return;
      }

      const response = await generateTokenTwoFactor({
        code: twoFactorCode,
        tempToken: tempAccessToken,
        email,
      });

      if (response && response.success && response.data) {
        const responseData = response.data as AuthDTO;

        // Save the final tokens using centralized function
        setTokens(
          responseData.accessToken ?? null,
          responseData.refreshToken ?? null,
          false // Normal login, not impersonation
        );
        navigateToLandingPage();
      } else {
        setErrorMessage(
          'Invalid two-factor authentication code. Please try again.',
        );
      }
    } catch (error) {
      console.error('Two factor auth verification error:', error);
      setErrorMessage(
        'Failed to verify two-factor authentication. Please try again.',
      );
    }
  };

  const handleGoogleSignIn = async (token: TokenExchangeDTO): Promise<void> => {
    try {
      setLoading(true);
      const response = await googleTokenExchange(token);
      setLoading(false);

      if (response) {
        handleSignInResponse(response, 'Unable to sign in with Google.');
      }
    } catch (error: unknown) {
      setLoading(false);
      if (error instanceof Error) {
        setErrorMessage(error.message);
      } else {
        setErrorMessage('An unexpected error occurred.');
      }
    }
  };

  const handleSignInResponse = useCallback((
    response: ApiResponse_string,
    errorMessage: SetStateAction<string | null>,
  ): void => {
    if (response && response.success && response.data) {
      setErrorMessage('');

      const responseData = response.data as AuthDTO;
      if (responseData.twoFactorEnabled) {
        // 2FA is enabled, show the 2FA input field
        setTempAccessToken?.(responseData.accessToken ?? '');
        setShowTwoFactorInput?.(true);
      } else {
        // Save tokens using centralized function
        setTokens(
          responseData.accessToken ?? null,
          responseData.refreshToken ?? null,
          false // Normal login, not impersonation
        );

        navigateToLandingPage();
      }
    } else {
      setErrorMessage(errorMessage);
    }
  }, [navigateToLandingPage, setTempAccessToken, setShowTwoFactorInput]);

  useEffect(() => {
    const token = getToken(); // Now checks sessionStorage first, then localStorage
    if (token) {
      navigateToLandingPage();
    }
  }, [navigateToLandingPage]);

  return {
    handleLogin,
    handleTwoFactorVerification,
    handleGoogleSignIn,
    handleSignInResponse,
    loading,
    errorMessage,
    setErrorMessage,
  };
};
